import React, { useEffect, useState } from "react";
import { Loader2, MessageSquarePlus, Star, Send, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DiaryHistory } from "./DiaryHistory";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { VoiceButton } from "./VoiceButton";
import { PhotoThumb } from "./Photos";

const HomeInfo = ({ h, sid, status }) => {
  if (!h) return <p className="mt-2 text-xs text-slate-500" data-testid={`home-info-${sid}`}>From home: not filled (counts as done)</p>;
  const ticked = h.saved || (h.ticks?.length || 0) + (h.done_items?.length || 0) > 0;
  const allDone = h.saved && !(h.class_not_done?.length) && !(h.hw_not_done?.length);
  const mismatch = allDone && (status === "not_done" || status === "no_homework");
  return (
    <div className="mt-2 space-y-1 rounded-md bg-sky-50 p-2 text-xs text-sky-900" data-testid={`home-info-${sid}`}>
      <p><b>From home:</b> {h.saved ? (allDone ? "everything done" : "filled the diary") : ticked ? "ticked work as done" : "not filled (counts as done)"}{h.mood ? ` · mood ${h.mood}` : ""}{h.note ? ` · extra: “${h.note}”` : ""}</p>
      {h.class_not_done?.length > 0 && <p className="font-semibold text-red-700" data-testid={`home-class-not-done-${sid}`}>Not done in class (per home): {h.class_not_done.join(" · ")}</p>}
      {h.hw_not_done?.length > 0 && <p className="font-semibold text-red-700" data-testid={`home-hw-not-done-${sid}`}>Homework not done: {h.hw_not_done.join(" · ")}</p>}
      {h.help && <p className="font-semibold text-red-700" data-testid={`home-help-${sid}`}>Needs help: {h.help}</p>}
      {mismatch && <p className="font-semibold text-amber-700" data-testid={`home-mismatch-${sid}`}>Home says done, you marked Nothing done in class — please check.</p>}
      {h.photos?.length > 0 && <div className="flex gap-1.5">{h.photos.map((p) => <PhotoThumb key={p} url={`/teacher/diary/photo/${p}`} testid={`t-photo-${p}`} />)}</div>}
    </div>
  );
};

export const STATUS = [
  { k: "done", label: "Done", cls: "bg-emerald-600 text-white" },
  { k: "partly", label: "Partly", cls: "bg-amber-400 text-amber-950" },
  { k: "not_done", label: "Nothing done in class", cls: "bg-red-600 text-white" },
  { k: "excellent", label: "Excellent", cls: "bg-blue-600 text-white" },
  { k: "no_homework", label: "Homework not done", cls: "bg-slate-700 text-white" },
  { k: "retest", label: "Retest / Redo", cls: "bg-violet-600 text-white" },
  { k: "pending", label: "Left with pending work", cls: "bg-orange-500 text-white" },
];

const giveStar = async (s) => {
  try { await api.post(`/teacher/students/${s.id}/stars`, { delta: 1, reason: "Class Diary: good work today" }); toast.success(`⭐ Star given to ${s.name}`); }
  catch (e) { toast.error(e?.response?.data?.detail || "Could not give star"); }
};

const Inform = ({ s, onClose }) => {
  const [text, setText] = useState(`${s.name} needs a little extra support. `);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/teacher/diary/inform", { student_id: s.id, text }); toast.success("Message sent to parent"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex items-center gap-2">
      <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} className="h-10 flex-1 rounded-md border border-slate-300 px-2 text-sm" data-testid={`inform-text-${s.id}`} />
      <VoiceButton onText={(t) => setText((x) => `${x} ${t}`)} testid={`inform-voice-${s.id}`} />
      <Button size="sm" onClick={send} disabled={busy} data-testid={`inform-send-${s.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}</Button>
    </div>
  );
};

const TestResult = ({ test, e, sid, onChange }) => (
  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs" data-testid={`test-result-${sid}`}>
    <span className="font-semibold text-violet-800">{test.name}:</span>
    {test.mode === "marks" ? <><input type="number" min="0" max={test.out_of} value={e.marks ?? ""} onChange={(ev) => onChange({ ...e, marks: ev.target.value === "" ? null : Number(ev.target.value) })}
      className="h-8 w-16 rounded border border-slate-300 px-1" data-testid={`marks-${sid}`} /><span>/ {test.out_of}</span></>
      : ["A", "B", "C", "D", "E"].map((gr) => <button key={gr} type="button" onClick={() => onChange({ ...e, grade: gr })} data-testid={`grade-${sid}-${gr}`}
        className={`h-7 w-7 rounded-full font-bold ${e.grade === gr ? "bg-violet-700 text-white" : "bg-slate-100 text-slate-600"}`}>{gr}</button>)}
  </div>
);

const Row = ({ s, e, absent, home, carry, test, onChange }) => {
  const [noteOpen, setNoteOpen] = useState(!!e.note);
  const [informOpen, setInformOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  if (absent) return (
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-400" data-testid={`result-row-${s.id}`}>
      <span>{s.name}</span><span className="text-xs font-semibold">Absent</span>
    </div>
  );
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`result-row-${s.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-800">{s.name} <span className="text-xs text-slate-400">{s.student_class}</span></span>
        <div className="flex flex-wrap gap-1">
          {STATUS.map((x) => (
            <button key={x.k} type="button" onClick={() => onChange({ ...e, status: x.k })} data-testid={`result-${s.id}-${x.k}`}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${e.status === x.k ? x.cls : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{x.label}</button>
          ))}
          <button type="button" onClick={() => setNoteOpen((v) => !v)} className="rounded-full p-1 text-slate-500 hover:text-slate-800" aria-label="Add note" data-testid={`result-note-btn-${s.id}`}><MessageSquarePlus className="h-4 w-4" /></button>
          <button type="button" onClick={() => giveStar(s)} className="rounded-full p-1 text-amber-500 hover:text-amber-600" aria-label="Give star" data-testid={`result-star-${s.id}`}><Star className="h-4 w-4" /></button>
          <button type="button" onClick={() => setInformOpen((v) => !v)} className="rounded-full p-1 text-sky-600 hover:text-sky-800" aria-label="Inform parent" data-testid={`result-inform-${s.id}`}><Send className="h-4 w-4" /></button>
          <button type="button" onClick={() => setHistOpen(true)} className="rounded-full p-1 text-slate-500 hover:text-slate-800" aria-label="Student history" data-testid={`result-history-${s.id}`}><History className="h-4 w-4" /></button>
          <Dialog open={histOpen} onOpenChange={setHistOpen}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto"><DialogHeader><DialogTitle>{s.name} — diary history</DialogTitle></DialogHeader>
              {histOpen && <DiaryHistory url={`/teacher/diary/history/${s.id}`} pdfUrl={`/teacher/diary/history/${s.id}/pdf`} photoBase="/teacher/diary/photo" />}</DialogContent>
          </Dialog>
        </div>
      </div>
      {carry && <p className="mt-1 text-xs font-semibold text-violet-700" data-testid={`carry-${s.id}`}>From {carry.date}: {STATUS.find((x) => x.k === carry.status)?.label}{carry.note ? ` — ${carry.note}` : ""}</p>}
      {test && <TestResult test={test} e={e} sid={s.id} onChange={onChange} />}
      {informOpen && <Inform s={s} onClose={() => setInformOpen(false)} />}
      {noteOpen && (
        <div className="mt-2 flex items-center gap-2">
          <input value={e.note} onChange={(ev) => onChange({ ...e, note: ev.target.value })} placeholder="Short note (optional)" maxLength={500}
            className="h-10 flex-1 rounded-md border border-slate-300 px-2 text-sm" data-testid={`result-note-${s.id}`} />
          <VoiceButton onText={(t) => onChange({ ...e, note: e.note ? `${e.note} ${t}` : t })} testid={`result-voice-${s.id}`} />
        </div>
      )}
      <HomeInfo h={home} sid={s.id} status={e.status} />
    </div>
  );
};

export const ResultsEditor = ({ day, klass, onSaved }) => {
  const [entries, setEntries] = useState({});
  const [busy, setBusy] = useState(false);
  const absent = new Set(day.absent);
  useEffect(() => {
    const saved = Object.fromEntries((day.saved?.entries || []).map((e) => [e.student_id, e]));
    setEntries(Object.fromEntries(day.students.map((s) => [s.id, saved[s.id] || { student_id: s.id, status: absent.has(s.id) ? "absent" : "done", note: "" }])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);
  const save = async () => {
    setBusy(true);
    try {
      const list = day.students.map((s) => (absent.has(s.id) ? { student_id: s.id, status: "absent", note: "" } : entries[s.id]));
      await api.post("/teacher/diary/day", { date: day.date, klass, entries: list });
      toast.success("Diary saved for today");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save diary"); } finally { setBusy(false); }
  };
  if (!day.students.length) return <p className="text-sm text-slate-500">No students in this class.</p>;
  return (
    <div className="space-y-2" data-testid="results-editor">
      <p className="text-xs text-slate-500">Everyone is <b>Done</b> by default — tap only the students who differ. Absent students (from attendance) are skipped.</p>
      {day.students.map((s) => entries[s.id] && <Row key={s.id} s={s} e={entries[s.id]} absent={absent.has(s.id)} home={day.home?.[s.id]} carry={day.carry?.[s.id]}
        test={day.plans.find((p) => p.test && p.student_ids.includes(s.id))?.test} onChange={(e) => setEntries((x) => ({ ...x, [s.id]: e }))} />)}
      <div className="flex items-center gap-3 pt-1">
        <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="results-save">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}{day.saved ? "Update diary" : "Save diary"}</Button>
        {day.saved && <span className="text-xs text-emerald-700" data-testid="results-saved-note">Saved · you can update it any time today</span>}
      </div>
    </div>
  );
};
